import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { MediaObject } from '@/domain/shared/media';
import { logoutUser } from '@/features/auth/authThunks';
import type { RootState } from '@/store/root-state';
import type { MediaCatalogState, MediaConfig, MediaState } from './mediaTypes';
import { deleteMedia, deleteMediaBatch, fetchSignedMediaUrls, uploadMedia } from './mediaThunks';

const initialCatalogState: MediaCatalogState = {
  byKey: {},
  allKeys: [],
};

const initialState: MediaState = {
  catalog: initialCatalogState,
  config: {
    ttlSeconds: 3600,
  },
};

function upsertMediaRecord(state: MediaState, media: MediaObject) {
  state.catalog.byKey[media.key] = {
    ...state.catalog.byKey[media.key],
    ...media,
  };

  if (!state.catalog.allKeys.includes(media.key)) {
    state.catalog.allKeys.push(media.key);
  }
}

function ensureMediaRecord(state: MediaState, key: string): MediaObject {
  const existing = state.catalog.byKey[key];
  if (existing) return existing;

  const created: MediaObject = { key };
  state.catalog.byKey[key] = created;
  state.catalog.allKeys.push(key);
  return created;
}

const mediaSlice = createSlice({
  name: 'media',
  initialState,
  reducers: {
    upsertMedia: (state, action: PayloadAction<MediaObject>) => {
      upsertMediaRecord(state, action.payload);
    },
    upsertMediaBatch: (state, action: PayloadAction<MediaObject[]>) => {
      action.payload.forEach((media) => upsertMediaRecord(state, media));
    },
    removeMediaByKey: (state, action: PayloadAction<string>) => {
      const key = action.payload;
      delete state.catalog.byKey[key];
      state.catalog.allKeys = state.catalog.allKeys.filter((item) => item !== key);
    },
    markMediaPendingDelete: (state, action: PayloadAction<{ key: string; pendingDelete: boolean }>) => {
      const media = ensureMediaRecord(state, action.payload.key);
      media.pendingDelete = action.payload.pendingDelete;
    },
    invalidateMediaKeys: (state, action: PayloadAction<string[]>) => {
      action.payload.forEach((key) => {
        const media = state.catalog.byKey[key];
        if (!media) return;
        media.expiresAt = 0;
      });
    },
    setMediaConfig: (state, action: PayloadAction<Partial<MediaConfig>>) => {
      state.config = {
        ...state.config,
        ...action.payload,
      };
    },
    clearMediaState: () => ({ ...initialState, catalog: { byKey: {}, allKeys: [] } }),
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSignedMediaUrls.pending, (state, action) => {
        action.meta.arg.filesKeys.forEach((key) => {
          const media = ensureMediaRecord(state, key);
          media.loading = true;
          media.savedToFolder = action.meta.arg.folder;
          media.error = undefined;
        });
      })
      .addCase(fetchSignedMediaUrls.fulfilled, (state, action) => {
        action.meta.arg.filesKeys.forEach((key) => {
          const media = ensureMediaRecord(state, key);
          media.loading = false;
        });

        action.payload.items.forEach((item) => {
          upsertMediaRecord(state, {
            ...item,
            loading: false,
          });
        });
      })
      .addCase(fetchSignedMediaUrls.rejected, (state, action) => {
        const errorMessage = action.payload?.message || action.error.message || 'Failed to fetch signed media URLs';

        action.meta.arg.filesKeys.forEach((key) => {
          const media = ensureMediaRecord(state, key);
          media.loading = false;
          media.error = errorMessage;
          media.savedToFolder = action.meta.arg.folder;
        });
      })
      .addCase(uploadMedia.pending, (state, action) => {
        upsertMediaRecord(state, {
          ...state.catalog.byKey[action.meta.arg.key],
          key: action.meta.arg.key,
          uploading: true,
          uploadingBar: 0,
          pendingDelete: false,
          error: undefined,
          savedToFolder: action.meta.arg.folder,
        });
      })
      .addCase(uploadMedia.fulfilled, (state, action) => {
        upsertMediaRecord(state, action.payload);
      })
      .addCase(uploadMedia.rejected, (state, action) => {
        upsertMediaRecord(state, {
          ...state.catalog.byKey[action.meta.arg.key],
          key: action.meta.arg.key,
          uploading: false,
          uploadingBar: 0,
          error: action.payload?.message || action.error.message || 'Failed to upload media',
          savedToFolder: action.meta.arg.folder,
        });
      })
      .addCase(deleteMedia.pending, (state, action) => {
        const media = ensureMediaRecord(state, action.meta.arg.key);
        media.pendingDelete = true;
        media.error = undefined;
      })
      .addCase(deleteMedia.fulfilled, (state, action) => {
        delete state.catalog.byKey[action.payload];
        state.catalog.allKeys = state.catalog.allKeys.filter((key) => key !== action.payload);
      })
      .addCase(deleteMedia.rejected, (state, action) => {
        const media = ensureMediaRecord(state, action.meta.arg.key);
        media.pendingDelete = false;
        media.error = action.payload?.message || action.error.message || 'Failed to delete media';
      })
      .addCase(deleteMediaBatch.pending, (state, action) => {
        action.meta.arg.keys.forEach((key) => {
          const media = ensureMediaRecord(state, key);
          media.pendingDelete = true;
          media.error = undefined;
        });
      })
      .addCase(deleteMediaBatch.fulfilled, (state, action) => {
        action.payload.deletedKeys.forEach((key) => {
          delete state.catalog.byKey[key];
        });
        state.catalog.allKeys = state.catalog.allKeys.filter((key) => !action.payload.deletedKeys.includes(key));

        action.payload.failed.forEach(({ key, error }) => {
          const media = ensureMediaRecord(state, key);
          media.pendingDelete = false;
          media.error = error;
        });
      })
      .addCase(deleteMediaBatch.rejected, (state, action) => {
        const errorMessage = action.payload?.message || action.error.message || 'Failed to delete media batch';

        action.meta.arg.keys.forEach((key) => {
          const media = ensureMediaRecord(state, key);
          media.pendingDelete = false;
          media.error = errorMessage;
        });
      })
      .addCase(logoutUser.fulfilled, () => ({ ...initialState, catalog: { byKey: {}, allKeys: [] } }));
  },
});

export default mediaSlice.reducer;

export const {
  upsertMedia,
  upsertMediaBatch,
  removeMediaByKey,
  markMediaPendingDelete,
  invalidateMediaKeys,
  setMediaConfig,
  clearMediaState,
} = mediaSlice.actions;

const selectMediaState = (state: RootState) => state.media;
const selectMediaCatalogState = createSelector(selectMediaState, (state) => state.catalog);

export const selectMediaConfig = createSelector(selectMediaState, (state) => state.config);

export const selectAllMedia = createSelector(selectMediaCatalogState, (catalog) =>
  catalog.allKeys.map((key) => catalog.byKey[key]).filter((item): item is MediaObject => Boolean(item)),
);

export const selectMediaByKey = createSelector(
  [selectMediaCatalogState, (_: RootState, key?: string | null) => key],
  (catalog, key) => {
    if (!key) return null;
    return catalog.byKey[key] ?? null;
  },
);

export const selectMediaByKeys = createSelector(
  [selectMediaCatalogState, (_: RootState, keys: string[]) => keys],
  (catalog, keys) => keys.map((key) => catalog.byKey[key]).filter((item): item is MediaObject => Boolean(item)),
);

export const selectMediaUrlByKey = createSelector([selectMediaByKey], (media) => media?.url ?? null);
